import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Loader2 } from 'lucide-react';
import { EDIT_REASONS } from './workbenchShared';

// Batch-fix selected sessions dialog (Jun 2026)
export const SessionFixDialog = ({ sessFix, onClose, form, setForm, onSave, saving }) => (
  <Dialog open={!!sessFix} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-md" data-testid="pw-sess-fix-dialog" onCloseAutoFocus={(e) => e.preventDefault()}>
      <DialogHeader>
        <DialogTitle>
          Fix {sessFix?.entryIds?.length} selected session{sessFix?.entryIds?.length === 1 ? '' : 's'}
        </DialogTitle>
        <DialogDescription>
          {sessFix?.worker?.worker_name} — each entry keeps its own date. Originals are preserved and revertible from the Adjustments banner.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div className="space-y-1">
          <Label className="text-xs">What to apply</Label>
          <select
            value={form.mode}
            onChange={(e) => setForm((f) => ({ ...f, mode: e.target.value }))}
            className="w-full px-3 py-2 rounded-md bg-[#0B1320] border border-[#1F2D3F] text-white text-sm"
            data-testid="pw-sess-fix-mode"
          >
            <option value="hours">Exact hours (clock-out becomes clock-in + hours)</option>
            <option value="exact">Exact start & end times (set shift)</option>
          </select>
        </div>
        {form.mode === 'hours' ? (
          <div className="space-y-1">
            <Label className="text-xs">Hours per session</Label>
            <Input
              type="number" min="0.25" max="24" step="0.25"
              value={form.hours}
              onChange={(e) => setForm((f) => ({ ...f, hours: e.target.value }))}
              data-testid="pw-sess-fix-hours"
            />
            <p className="text-[11px] text-slate-500">e.g. 8 — every checked entry becomes exactly 8h from its clock-in, wiping rounding errors.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Shift start</Label>
              <Input
                type="time" value={form.start_time}
                onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))}
                data-testid="pw-sess-fix-start"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Shift end</Label>
              <Input
                type="time" value={form.end_time}
                onChange={(e) => setForm((f) => ({ ...f, end_time: e.target.value }))}
                data-testid="pw-sess-fix-end"
              />
            </div>
            <p className="text-[11px] text-slate-500 col-span-2">Both clock-in and clock-out snap to these times on each entry&apos;s own date. End before start = overnight shift.</p>
          </div>
        )}
        <div className="space-y-1">
          <Label className="text-xs">Reason (required for the audit trail)</Label>
          <select
            value={form.reason_kind}
            onChange={(e) => setForm((f) => ({ ...f, reason_kind: e.target.value }))}
            className="w-full px-3 py-2 rounded-md bg-[#0B1320] border border-[#1F2D3F] text-white text-sm"
            data-testid="pw-sess-fix-reason"
          >
            {EDIT_REASONS.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Notes (optional)</Label>
          <Textarea
            rows={2}
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            placeholder="e.g. Rounding cleanup before June 15 payroll"
            data-testid="pw-sess-fix-notes"
          />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} data-testid="pw-sess-fix-cancel">Cancel</Button>
        <Button
          onClick={onSave}
          disabled={saving || !form.reason_kind}
          className="bg-amber-500 hover:bg-amber-600 text-white"
          data-testid="pw-sess-fix-save"
        >
          {saving ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Applying…</> : `Fix ${sessFix?.entryIds?.length || 0} session(s)`}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
